/* jshint esversion: 6 */
import * as constants from 'app/redux/constants.js';
import { defaultState } from 'app/redux/defaultState.js';
import FilterModule, { isFilterActive } from 'app/mixins/FilterModule.js';
import SortModule from 'app/mixins/SortModule.js';
import * as _ from 'underscore';
import Utilities from 'app/utility/Utilities.js';



/**
 * A list reload must not downgrade an input already fetched in full.
 *
 * The list carries a summary of each input; INPUT_SHOW swaps in the full
 * record (`attrs`, filters, schedule), and the settings screen diffs its
 * draft against that record to decide what is dirty. A page load requests the
 * list twice - App and InputEdit both call getRoutes, and the epic runs both -
 * so when the second response landed after INPUT_SHOW it replaced the full
 * record with the summary, and nearly every tab read as changed. Intermittent,
 * because it depends on which response wins.
 *
 * So a summary takes the full record's fields where there is one, keeping its
 * own for what only the list carries (errCount, lastPullTs, the icon).
 */
const keepShownInputs = (routes, previous) => {
    let shown = {};

    (previous || []).forEach((route) => {
        ((route && route.inputs) || []).forEach((input) => {
            if (input && input.attrs !== undefined) {
                shown[parseInt(input.id, 10)] = input;
            }
        });
    });

    if (!Object.keys(shown).length || !Array.isArray(routes)) {
        return routes;
    }

    return routes.map((route) => {
        if (!route || !Array.isArray(route.inputs)) {
            return route;
        }

        return {
            ...route,
            inputs: route.inputs.map((input) => {
                let full = input ? shown[parseInt(input.id, 10)] : undefined;

                return (full && input.attrs === undefined) ? {...input, ...full} : input;
            })
        };
    });
};

/**
 * The routes a stored filter keeps, in list order. Filtering for plain feeds
 * (INPUT_TYPE_FEED, 100) leaves out the subtypes that share the type, such as
 * Pinterest.
 */
const filterRoutes = (list, filter) => {
    let filtered = FilterModule(list, filter.key, filter.value, filter.exact);

    if (filter.value == 100) {
        filtered = _.filter(filtered, (route) => !route.inputs.some((input) => input.subtype));
    }

    return filtered;
};

export const automatedRoutes = (state = defaultState.automatedRoutes, action) => {
    let nextState = Utilities.deepClone(state),
        i = 0,
        j = 0,
        routes = [],
        getRouteTypeCount = (routes) => {
            let inputAggregate = {},
                i = 0,
                j = 0,
                input = null,
                name = null,
                inputTotal = 0,
                inputCount = [];

            // Walk the route collection and aggregate the counts
            // of each type of input (feed, twitter, insta, etc)
            for (i = 0; i < routes.length; i++){
                for (j = 0; j < routes[i].inputs.length; j++) {
                    input = routes[i].inputs[j];

                    // first check for a subtype
                    if (input.subtype) {
                        if (!inputAggregate[input.subtype]) {
                            inputAggregate[input.subtype] = 0;
                        }
                        inputAggregate[input.subtype]++;
                        inputTotal++;
                        continue;
                    }

                    // check if the particular input count has been initialized
                    if (!inputAggregate[input.type]) {
                        inputAggregate[input.type] = 0;
                    }

                    inputAggregate[input.type]++;
                    inputTotal++;
                }
            }

            // create an array of objects to be consumed by the filter drop down:
            // { type: 'Twitter', count: 1 }
            for (let key in inputAggregate) {
                if (key !== "") {
                    inputCount.push({
                        type: key,
                        count: inputAggregate[key]
                    });
                }
            }

            if (inputCount.length > 1) {
                inputCount.push({
                    'type': '',
                    count: inputTotal
                });
            }

            return inputCount;
        };

    switch (action.type) {
        case constants.UTILITIES_FILTER_STORE:
            if (action.store === 'routes') {
                nextState.filter = {
                    key: action.filter,
                    value: action.value,
                    exact: action.exact
                };
            }
            break;

        case constants.UTILITIES_SORT_STORE:
            if (action.store === 'routes') {
                nextState.sort = {
                    key: action.key,
                    direction: action.direction
                };
                nextState.list = SortModule(nextState.list, action.key, action.direction);
            }
            break;

        case constants.UTILITIES_CLEAR_FILTER:
            if (action.store === 'routes') {
                nextState.filter = {};
            }
            break;

        case constants.ROUTE_LIST:
            if (action.status === constants.STATE_SUCCESS) {
                nextState.list = keepShownInputs(action.payload, state.list);
                nextState.types = getRouteTypeCount(nextState.list);
            }
            break;

        case constants.ROUTE_SHOW:
        case constants.ROUTE_UPDATE:
            if (action.status === constants.STATE_SUCCESS) {
                if (nextState.list.length) {
                    for (i = 0; i < nextState.list.length; ++i) {
                        if (parseInt(nextState.list[i].id, 10) === parseInt(action.payload.id, 10)) {
                            nextState.list[i] = action.payload;
                            break;
                        }
                    }
                } else {
                    nextState.list.push(action.payload);
                }
            }
            break;

        case constants.ROUTE_DELETE:
            if (action.status === constants.STATE_SUCCESS) {
                for (i = 0; i < nextState.list.length; ++i) {
                    if (nextState.list[i].id != action.payload.id) {
                        routes.push(nextState.list[i]);
                    }
                }
                nextState.list = routes;
            }
            break;

        case constants.ROUTE_CREATE:
            if (action.status === constants.STATE_SUCCESS) {
                nextState.list.push(action.payload);
            }
            break;

        case constants.INPUT_SHOW:
        case constants.INPUT_UPDATE:
        case constants.INPUT_REFRESH:
            if (action.status === constants.STATE_SUCCESS) {
                for (i = 0; i < nextState.list.length; ++i) {
                    let inputs = nextState.list[i].inputs;
                    for (j = 0; j < inputs.length; ++j) {
                        if (parseInt(inputs[j].id, 10) === parseInt(action.payload.id, 10)) {
                            /* Keep the list's icon. Route.php builds it for the
                               automations list; Input::show_api, which answers
                               the fetch, update and refresh, sends none, so the
                               settings avatar fell back to initials the moment
                               the screen fetched its own copy. */
                            inputs[j] = action.payload.icon === undefined
                                ? {...action.payload, icon: inputs[j].icon}
                                : action.payload;
                            break;
                        }
                    }
                }
            }
            break;

        case constants.INPUT_CREATE:
            if (action.status === constants.STATE_SUCCESS) {
                for (i = 0; i < nextState.list.length; ++i) {
                    if (parseInt(nextState.list[i].igID, 10) === parseInt(action.payload.igID, 10)) {
                        nextState.list[i].inputs.push(action.payload);
                        break;
                    }
                }
            }
            break;

        case constants.INPUT_DELETE:
            // by the input's own id: a route ROUTE_LIST's summary gave carries no igID to match on
            if (action.status === constants.STATE_SUCCESS) {
                for (i = 0; i < nextState.list.length; ++i) {
                    if (Array.isArray(nextState.list[i].inputs)) {
                        nextState.list[i].inputs = nextState.list[i].inputs.filter((input) => parseInt(input.id, 10) !== parseInt(action.payload.id, 10));
                    }
                }
            }
            break;

        case constants.INPUT_VALIDATE:
            if (action.status === constants.STATE_SUCCESS) {
                action.payload.inputValid = true;
                if (action.payload.igID) {
                    for (i = 0; i < nextState.list.length; ++i) {
                        if (parseInt(nextState.list[i].rID, 10) === parseInt(action.payload.rID, 10)) {
                            let inputs = nextState.list[i].inputs;
                            for (j = 0; j < inputs.length; ++j) {
                                if (parseInt(inputs[j].id, 10) === parseInt(action.payload.id, 10)) {
                                    inputs[j] = action.payload;
                                    break;
                                }
                            }
                        }
                    }
                }
                nextState.validInputs.push(action.payload);
            } else {
                nextState.validInputs = [];
            }
            break;

        case constants.INPUT_CLEAR_VALIDATION:
            nextState.validInputs = [];
            break;

        case constants.INPUT_LOG:
            if (action.status === constants.STATE_SUCCESS) {
                if (_.findWhere(nextState.logs, {'id': action.payload.id}) !== undefined) {
                    for (i = 0; i < nextState.logs.length; ++i) {
                        if (parseInt(nextState.logs[i].id, 10) === parseInt(action.payload.id, 10)) {
                            nextState.logs[i] = action.payload;
                            break;
                        }
                    }
                } else {
                    nextState.logs.push(action.payload);
                }
            }
            break;

        case constants.INPUT_LIST_SOCIAL_CREDS:
            if (action.status === constants.STATE_SUCCESS) {
                nextState.socialInputCreds = action.payload;
            }
            break;

        case constants.OUTPUT_SHOW:
        case constants.OUTPUT_UPDATE:
            if (action.status === constants.STATE_SUCCESS) {
                for (i = 0; i < nextState.list.length; ++i) {
                    if (parseInt(nextState.list[i].ogID, 10) === parseInt(action.payload.ogID, 10)) {
                        for (j = 0; j < nextState.list[i].outputs.length; ++j) {
                            if (parseInt(nextState.list[i].outputs[j].id, 10) === parseInt(action.payload.id, 10)) {
                                nextState.list[i].outputs[j] = action.payload;
                                break;
                            }
                        }
                    }
                }
            }
            break;

        case constants.OUTPUT_DELETE:
            if (action.status === constants.STATE_SUCCESS) {
                for (i = 0; i < nextState.list.length; ++i) {
                    if (parseInt(nextState.list[i].ogID, 10) === parseInt(action.payload.ogID, 10)) {
                        let outputs = nextState.list[i].outputs,
                            nextOutputs = [];
                        for (j = 0; j < outputs.length; ++j) {
                            if (parseInt(outputs[j].id, 10) !== parseInt(action.payload.id, 10)) {
                                nextOutputs.push(outputs[j]);
                            }
                        }
                        nextState.list[i].outputs = nextOutputs;
                    }
                }
            }
            break;

        case constants.OUTPUT_CREATE:
            if (action.status === constants.STATE_SUCCESS) {
                for (i = 0; i < nextState.list.length; ++i) {
                    if (parseInt(nextState.list[i].ogID, 10) === parseInt(action.payload.ogID, 10)) {
                        nextState.list[i].outputs.push(action.payload);
                        break;
                    }
                }
            }
            break;

        /*
         * The two flags that drive the badge and the avatar: the route detail
         * draws this embedded copy of each output's social, and a social write
         * only ever lands in socials.list.
         * Without this a reconnect confirmed through the takeover prompt, or a
         * reactivate from the avatar, leaves the row grey until the route is
         * fetched again. The toggle never showed it only because it also writes
         * the output, which comes back carrying a fresh copy.
         *
         * Merged rather than replaced: the embedded copy is the route payload's
         * own shape and the response is the full record.
         */
        case constants.SOCIAL_SHOW:
        case constants.SOCIAL_UPDATE:
            if (action.status === constants.STATE_SUCCESS && action.payload && !action.payload.deletedFlag && !action.superseded) {
                let syncEmbeddedSocial = (list) => {
                    for (let r = 0; r < list.length; ++r) {
                        for (let o = 0; o < list[r].outputs.length; ++o) {
                            if (parseInt(list[r].outputs[o].social.id, 10) === parseInt(action.payload.id, 10)) {
                                list[r].outputs[o].social = Object.assign({}, list[r].outputs[o].social, action.payload);
                                break;
                            }
                        }
                    }
                };

                syncEmbeddedSocial(nextState.list);
            }
            break;

        default:
            break;
    }

    /*
     * A search's or type filter's results are derived from list rather than
     * kept beside it. The clone above severs the two on every action, so a
     * change written only to list (a pause, a delete, a destination added)
     * would never reach the filtered page. Recomputed on the filter and sort
     * actions and on every response, which is when list changes.
     */
    if (!isFilterActive(nextState.filter)) {
        nextState.filteredList = [];
    } else if (action.status === constants.STATE_SUCCESS ||
        action.type === constants.UTILITIES_FILTER_STORE ||
        action.type === constants.UTILITIES_SORT_STORE) {
        nextState.filteredList = filterRoutes(nextState.list, nextState.filter);
    }

    return nextState;
};
